import { useState, useEffect } from 'react';
import { checkSubscriptionStatus, type SubscriptionInfo } from '../utils/subscription';
import { useNavigate } from 'react-router-dom';

interface PaymentRequiredProps {
  userId: string;
  onUpgradeClick?: (plan: string) => void;
}

export default function PaymentRequired({ userId, onUpgradeClick }: PaymentRequiredProps) {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  useEffect(() => {
    console.log('🔍 [PAYMENT_REQUIRED] Component mounted for user:', userId);
    console.log('🔍 [PAYMENT_REQUIRED] Initial subscription state:', subscription);
    loadSubscriptionStatus();
    loadUserInfo();
  }, [userId]);

  const loadSubscriptionStatus = async () => {
    try {
      console.log('🔍 [PAYMENT_REQUIRED] Loading subscription status for user:', userId);
      setLoading(true);
      const subscriptionInfo = await checkSubscriptionStatus(userId);
      console.log('📊 [PAYMENT_REQUIRED] Subscription info received:', subscriptionInfo);
      setSubscription(subscriptionInfo);
    } catch (error) {
      console.error('❌ [PAYMENT_REQUIRED] Error loading subscription:', error);
    } finally {
      console.log('✅ [PAYMENT_REQUIRED] Subscription loading completed');
      setLoading(false);
    }
  };

  const loadUserInfo = () => {
    console.log('🔍 [PAYMENT_REQUIRED] Loading user info from localStorage...');
    const sessionData = localStorage.getItem('user_session');
    console.log('📊 [PAYMENT_REQUIRED] Session data from localStorage:', sessionData);
    
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData);
        console.log('✅ [PAYMENT_REQUIRED] User info loaded:', {
          id: session.id,
          email: session.email,
          plan: session.plan,
          shelter_name: session.shelter_name
        });
        setUserInfo(session);
      } catch (error) {
        console.error('❌ [PAYMENT_REQUIRED] Error parsing user session:', error);
      }
    } else {
      console.log('❌ [PAYMENT_REQUIRED] No session data found in localStorage');
    }
  };

  const handleLogout = () => {
    // Clear user session
    localStorage.removeItem('user_session');
    localStorage.removeItem('user_data');
    
    // Redirect to home page
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const handlePlanSelect = async (plan: string) => {
    try {
      setUpgrading(true);
      
      // Call the parent's upgrade handler if provided
      if (onUpgradeClick) {
        await onUpgradeClick(plan);
        return;
      }

      // Default upgrade functionality using Stripe
      console.log('Creating checkout session for:', {
        uid: userId,
        customer_email: userInfo?.email,
        plan: plan
      });

      const response = await fetch('https://huellitas-unidas-server.vercel.app/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: userId,
          customer_email: userInfo?.email || '',
          plan: plan,
          priceId: 'price_1SFidZH4z73AhjcjKL6d2fyJ' // Premium plan price ID
        }),
      });

      console.log('Checkout response status:', response.status);
      const data = await response.json();
      console.log('Checkout response data:', data);

      if (!response.ok) {
        if (data.error === "User already has an active subscription.") {
          throw new Error("Ya tienes una suscripción activa. Si necesitas ayuda, contacta al soporte.");
        }
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      if (data.url) {
        // Keep loading state active during redirect
        window.location.href = data.url;
        
        // Set a timeout to stop loading if redirect fails
        setTimeout(() => {
          setUpgrading(false);
        }, 10000); // 10 seconds timeout
        
        return;
      } else {
        throw new Error('No se pudo crear la sesión de pago - URL no recibida');
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      alert(`Error al procesar la actualización: ${error instanceof Error ? error.message : 'Error desconocido'}. Por favor, inténtalo de nuevo.`);
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-300 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Cargando información de suscripción...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-orange-50 to-blue-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              ¡Hola <span className="text-orange-500">{userInfo?.shelter_name || 'Usuario'}</span>! 👋
            </h2>
            <p className="text-lg text-gray-600 mb-2">
              ¡Gracias por unirte a <span className="text-orange-500 font-semibold">Huellitas Unidas</span>!
            </p>
            <p className="text-gray-600">
              Para comenzar a usar el panel de administración y salvar más vidas, por favor selecciona un plan que se adapte a las necesidades de tu refugio.
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                {subscription?.subscriptionStatus === 'trialing' ? 'Actualizar Suscripción' : 'Elige Tu Plan'}
              </h1>
              <p className="text-gray-600">
                {subscription?.subscriptionStatus === 'trialing' 
                  ? 'Tu período de prueba está terminando. Elige un plan para continuar salvando vidas.'
                  : 'Comienza gratis y escala según las necesidades de tu refugio.'
                }
              </p>
            </div>
            
            {/* User Account */}
            <div className="user-dropdown-container relative">
              <button 
                className="flex items-center space-x-3 px-4 py-2 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-orange-700">
                    {(userInfo?.shelter_name || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {userInfo?.shelter_name || 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500">{userInfo?.email || ''}</p>
                </div>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showUserDropdown && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cuenta</p>
                    <p className="text-sm text-gray-900 truncate mt-1">{userInfo?.email || ''}</p>
                  </div>
                  
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Plans Section */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className={`grid gap-8 ${subscription?.isTrialActive || !subscription || !subscription?.isActive ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md mx-auto'}`}>
          {/* Trial Plan */}
          {(subscription?.isTrialActive || !subscription || !subscription?.isActive) && (
            <div className="border border-blue-200 rounded-xl p-8 hover:border-blue-300 transition-colors bg-white flex flex-col h-full">
              <div className="text-center mb-8">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Plan Prueba</h3>
                <p className="text-gray-600 text-sm mb-4">
                  {subscription?.isTrialActive 
                    ? 'Acceso completo durante el período de prueba' 
                    : 'Acceso completo por 30 días gratis'
                  }
                </p>
                <div className="text-2xl font-bold text-gray-900">Gratis</div>
                <div className="text-sm text-gray-500">30 días</div>
              </div>

              <div className="space-y-3 mb-8 flex-grow">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 text-sm">Acceso completo durante 30 días</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 text-sm">Todas las funciones disponibles</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 text-sm">Sin limitaciones</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 text-sm">Soporte completo</span>
                </div>
              </div>

              <div className="text-center mt-auto">
                {subscription?.isTrialActive ? (
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Estado actual</div>
                    <div className="text-sm font-medium text-gray-900">
                      {subscription?.trialEndDate ? 
                        `Termina el ${new Date(subscription.trialEndDate).toLocaleDateString()}` : 
                        'Período de prueba activo'
                      }
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => onUpgradeClick && onUpgradeClick('trial')}
                    disabled={upgrading}
                    className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                  >
                    {upgrading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Procesando...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h8a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2z" />
                        </svg>
                        Comenzar Prueba Gratis
                      </>
                    )}
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-3 opacity-0">Placeholder for alignment</p>
              </div>
            </div>
          )}

          {/* Premium Plan */}
          <div className="border border-orange-200 rounded-xl p-8 hover:border-orange-300 transition-colors relative bg-white flex flex-col h-full">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                Recomendado
              </span>
            </div>
            
            <div className="text-center mb-8">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Plan Premium</h3>
              <p className="text-gray-600 text-sm mb-4">Acceso completo y funciones avanzadas</p>
              <div className="text-2xl font-bold text-gray-900">$299</div>
              <div className="text-sm text-gray-500">por mes</div>
            </div>

            <div className="space-y-3 mb-8 flex-grow">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-sm">Campañas de donación ilimitadas</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-sm">Casos de adopción ilimitados</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-sm">Panel de administración completo</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-sm">Integración con redes sociales</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-sm">Herramientas de marketing</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-700 text-sm">Soporte prioritario</span>
              </div>
            </div>

            <div className="text-center mt-auto">
              <button
                onClick={() => handlePlanSelect('premium')}
                disabled={upgrading}
                className={`w-full py-3 px-6 rounded-lg transition-colors font-medium ${
                  upgrading 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {upgrading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    Actualizar a Premium
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 mt-3">
                Pago seguro procesado por Stripe
              </p>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-2xl mx-auto">
            <div className="flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="font-medium text-gray-900">Información Importante</h4>
            </div>
            <p className="text-gray-600 text-sm">
              {subscription?.subscriptionStatus === 'trialing' 
                ? 'Tu período de prueba está terminando. Actualiza tu suscripción para mantener el acceso completo a todas las funciones y continuar salvando vidas.'
                : '¡Estás a un paso de comenzar a salvar vidas! Una vez que elijas tu plan, tendrás acceso inmediato a todas las herramientas para gestionar tu refugio.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}